import { Platform } from "react-native";
import { Masks } from "react-native-mask-input";
import { XStack, YStack } from "tamagui";

import {
  PHONE_LENGTH,
  USA_COUNTRY_CODE,
  getSmsOptInText,
} from "@/auth/usePhoneCodeAuth";
import { Link, Paragraph, Text } from "@/components";
import { MaskedInput } from "@/components/forms/Input";
import {
  BORDERLESS_INPUT,
  OptionSeparator,
  PencilBadge,
} from "@/conversation/AskUserQuestionsPanel/OptionParts";
import PanelHeader from "@/conversation/AskUserQuestionsPanel/PanelHeader";
import SkipOrSubmit from "@/conversation/AskUserQuestionsPanel/SkipOrSubmit";

const PHONE_TITLE = "What's your phone number?";
const PHONE_PLACEHOLDER = "Enter your phone number";

const noop = () => {};

export default function PhoneNumberCard({
  phone,
  onChangePhone,
  onSubmit,
  isSending = false,
}: {
  phone: string;
  onChangePhone: (phone: string) => void;
  onSubmit: () => void;
  isSending?: boolean;
}) {
  const canSubmit = phone.length === PHONE_LENGTH && !isSending;

  const submit = () => {
    if (!canSubmit) return;

    onSubmit();
  };

  return (
    <>
      <PanelHeader
        question={PHONE_TITLE}
        index={0}
        total={1}
        onPrev={noop}
        onNext={noop}
      />
      <XStack
        alignItems="center"
        gap="$100"
        paddingHorizontal="$150"
        paddingBottom="$150"
      >
        <PencilBadge />
        <MaskedInput.Controlled
          flex={1}
          rootProps={{ flex: 1 }}
          frameProps={BORDERLESS_INPUT}
          prefix={<Text>{USA_COUNTRY_CODE}</Text>}
          placeholder={PHONE_PLACEHOLDER}
          mask={Masks.USA_PHONE}
          inputMode="tel"
          autoComplete="tel-national"
          textContentType="telephoneNumber"
          autoFocus
          value={phone}
          onChangeText={(_, unmasked) => onChangePhone(unmasked)}
          onSubmitEditing={submit}
        />
      </XStack>
      {/* Clerk mounts its bot check here when a sign-up starts on web. */}
      {Platform.OS === "web" && <div id="clerk-captcha" />}
      <OptionSeparator />
      <XStack
        alignItems="center"
        gap="$200"
        paddingHorizontal="$150"
        paddingTop="$150"
        paddingBottom="$150"
      >
        <YStack flex={1} gap="$100">
          <Paragraph size="$xs" color="$textSecondary">
            {/* "continuing" rather than "clicking 'Continue'": this card submits with an arrow. */}
            {getSmsOptInText("continuing")}
          </Paragraph>
          <XStack gap="$100" alignItems="center">
            <Link
              href="/privacy"
              size="$xs"
              target="_blank"
              color="$textPrimary"
              aria-label="Privacy policy (opens in new tab)"
              dataTrackingId="pre-async-setup-privacy-policy"
            >
              Privacy policy
            </Link>
            <Text size="$xs" color="$textSecondary">
              |
            </Text>
            <Link
              href="/terms"
              size="$xs"
              target="_blank"
              color="$textPrimary"
              aria-label="Terms of use (opens in new tab)"
              dataTrackingId="pre-async-setup-terms-of-use"
            >
              Terms of use
            </Link>
          </XStack>
        </YStack>
        <SkipOrSubmit
          showSkip={false}
          showSubmit
          submitDisabled={!canSubmit}
          isLast={false}
          onSkip={noop}
          onSubmit={submit}
        />
      </XStack>
    </>
  );
}
